// -----------------------------------------------------------------------------
// src/components/dashboard/components/popups/ClientCampaignsDialog.js
// -----------------------------------------------------------------------------
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, Divider, Box, Stack,
  Typography, CircularProgress, Chip, IconButton, useTheme
} from "@mui/material";
import { Close, Campaign as CampaignIcon } from "@mui/icons-material";
import { Link } from "react-router-dom";
import client from "../../../../API";

/* ─────────── helpers ─────────── */
const DialogShell = ({ title, icon, onClose, children }) => (
  <Dialog open onClose={onClose} fullWidth maxWidth="md"
          PaperProps={{ sx:{ borderRadius:4, overflow:"hidden" } }}>
    <DialogTitle sx={{ px:3, py:2 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        {icon}
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Box flexGrow={1}/>
        <IconButton onClick={onClose} size="small"><Close/></IconButton>
      </Stack>
    </DialogTitle>
    <Divider/>
    {children}
  </Dialog>
);

export default function ClientCampaignsDialog({ popup, setPopup }) {
  const theme     = useTheme();
  const clientId  = popup?.client?.client_id ?? popup?.client?.id ?? null;

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [camp,    setCamp]    = useState([]);

  useEffect(() => {
    if (!clientId) return;
    (async () => {
      setLoading(true); setError(null);
      try {
        const { campaigns=[] } = await client.users.getClientCampaigns(clientId);
        setCamp(campaigns);
      } catch (e) {
        console.error("[ClientCampaignsDialog] fetch error", e);
        setError(e); setCamp([]);
      } finally { setLoading(false); }
    })();
  }, [clientId]);

  /* ─────────── UI ─────────── */
  const close = () => setPopup({ type:null });

  return (
    <DialogShell
      title={`${popup.client?.full_name ?? "Client"}'s Campaigns`}
      icon={<CampaignIcon sx={{ color:"primary.main" }}/>}
      onClose={close}
    >
      <DialogContent sx={{ p:3 }}>
        {loading
          ? <Box sx={{ display:"grid", placeItems:"center", height:200 }}>
              <CircularProgress/>
            </Box>
          : error
              ? <Typography color="error" align="center">
                  Failed to load campaigns
                </Typography>
              : camp.length===0
                  ? <Typography variant="body2" color="text.secondary" align="center">
                      No campaigns yet
                    </Typography>
                  : camp.map(c=>(
                      <Link key={c.mapping_id} to={`/campaigns/${c.campaign_id}`}
                            style={{ textDecoration:"none" }}>
                        <Box sx={{
                          p:3, mb:2, borderRadius:3, bgcolor:"background.paper",
                          boxShadow:theme.shadows[1],
                          "&:hover":{ boxShadow:theme.shadows[3] },
                        }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box sx={{
                              width:8, height:40,
                              bgcolor:c.status==="active"?"success.main":"error.main",
                              borderRadius:1,
                            }}/>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {c.campaign_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {c.date_key}• $
                                {c.amount?.toLocaleString?.() ?? "N/A"}
                              </Typography>
                            </Box>
                            <Box flexGrow={1}/>
                            <Chip label={c.status} size="small"
                                  color={c.status==="active"?"success":"error"}
                                  sx={{ borderRadius:1 }}/>
                          </Stack>
                        </Box>
                      </Link>
                    ))}
      </DialogContent>
    </DialogShell>
  );
}
